import './ProductInfos.scss';

import cn from 'classnames';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import ButtonLoading from '@/common/components/molecules/ButtonLoading/ButtonLoading';
import Quantity from '@/common/components/molecules/Quantity/Quantity';
import {
  ALTERNATIVE_TYPE,
  BREAKPOINTS,
  ROLES,
  ROUTE_PRODUCT,
  SOURCE,
} from '@/common/constants';
import { useAddToContext } from '@/common/contexts/AddToContext';
import { useAuth } from '@/common/contexts/AuthContext';
import { useDefaultCartContext } from '@/common/contexts/DefaultCartContext';
import useStocks from '@/common/hooks/stocks/useStocks';
import { ReactComponent as BasketSvg } from '@/ebat/assets/icons/basket.svg';
import { TAG_MANAGER } from '@/ebat/constants';

import ProductPrice from '../../Common/Product/ProductPrice/ProductPrice';
import Stocks from '../../Common/Stocks/Stocks';
import AgrementFgaz from '../AgrementFgaz/AgrementFgaz';
import AlternativeProduct from '../AlternativeProduct/AlternativeProduct';
import ConfigLinks from '../ConfigLinks/ConfigLinks';
import Energetics from '../Energetics/Energetics';
import ProductDescription from '../ProductDescription/ProductDescription';
import {
  ProductInfosDefaultProps,
  ProductInfosPropTypes,
} from './ProductInfos.proptypes';

const ProductInfos = ({
  agrementFgaz,
  bundleParents,
  brand,
  categories,
  className,
  codeOldCompanySku,
  configurations,
  companyAvailability,
  complementaryDescription,
  customerPrice,
  discountClasses,
  discountPrice,
  discountPriceTTC,
  energetics,
  exclusivity,
  flags,
  hasPrice,
  hasProductDiff,
  hasStock,
  id,
  image,
  imageSize,
  invoiceUnit,
  isBundle,
  isConfigProduct,
  isDisabled,
  isFavorite,
  isLightDisplay,
  lengthName,
  mainSku,
  manufacturerSku,
  name,
  onAddPredictions,
  path,
  productDiff,
  publicPrice,
  publicPriceTTC,
  quantityFieldRef,
  related,
  range,
  salesPackaging,
  saleUnit,
  slug,
  splitPackaging,
  stocks,
  stockStatus,
  sku,
  substitute,
  weeeAmount,
}) => {
  const { t } = useTranslation();
  const { offsetWidth } = document.body;
  const { addToCart } = useAddToContext();
  const { defaultCart } = useDefaultCartContext();

  const { user, isShowNetPrice } = useAuth();
  const { getTotalStock } = useStocks(stocks);

  const isClearance = () =>
    flags.filter((flag) => flag.slug === 'clearance').length > 0;

  /**
   * Build an alternative product url
   * @type {string}
   */
  const urlAlternativeProduct = (newPath, newSlug) =>
    `${ROUTE_PRODUCT}/${newPath}/${newSlug}`;

  /**
   * Call AI and push dataLayer after add to cart success
   * @param {object} data
   * @type {function(): void}
   */
  const onAddToCartSuccess = useCallback(
    ({ data }) => {
      onAddPredictions({
        ...data[0],
        quantity: quantityFieldRef.current.value,
        product: {
          addToCartOptions: { options: { linkUrl: null } },
          brand,
          configurations,
          customerPrice,
          discountPrice,
          exclusivity,
          flags,
          hasPrice, // withPriceAndStock
          hasStock, // withPriceAndStock
          id,
          image,
          imageSize,
          invoiceUnit,
          isFavorite,
          mainSku, // withPriceAndStock
          manufacturerSku,
          name,
          onAddPredictions,
          path,
          publicPrice,
          saleUnit,
          salesPackaging,
          sku,
          slug,
          splitPackaging,
          stockStatus,
          stocks,
          weeeAmount,
        },
      });
    },
    [
      brand,
      configurations,
      customerPrice,
      discountPrice,
      exclusivity,
      flags,
      hasPrice,
      hasStock,
      id,
      image,
      imageSize,
      invoiceUnit,
      isFavorite,
      mainSku,
      manufacturerSku,
      name,
      onAddPredictions,
      path,
      publicPrice,
      quantityFieldRef,
      saleUnit,
      salesPackaging,
      sku,
      slug,
      splitPackaging,
      stockStatus,
      stocks,
      weeeAmount,
    ],
  );

  /**
   * Add item to cart
   * @type {(...args: any[]) => any}
   */
  const addCartItemsAction = () => {
    addToCart({
      items: [
        {
          quantity: quantityFieldRef.current.value,
          sku,
          name,
          id,
          brand,
          source: SOURCE.PRODUCT_DETAIL,
        },
      ],
      cart: { ...defaultCart },
      onSuccessCallback: (data) => {
        const items = data?.items?.filter((item) => item.sku === sku) ?? [];
        if (items.length > 0) {
          onAddToCartSuccess({ data: items });
        }
      },
      options: {
        dataLayer: { origin: TAG_MANAGER.ORIGINS.PRODUCT },
      },
    });
  };

  const hasBundleParents = () => !isBundle && bundleParents?.length > 0;

  return (
    <div className={cn(className, 'product-infos')} data-qa-id="product-infos">
      <div
        className="product-infos__description"
        data-qa-id="product-infos-description"
      >
        <ProductDescription
          brand={brand}
          className="product-infos__title"
          codeOldCompanySku={codeOldCompanySku}
          complementaryDescription={complementaryDescription}
          discountClasses={discountClasses}
          lengthName={lengthName}
          manufacturerSku={manufacturerSku}
          name={name}
          range={range}
          sku={sku}
        />

        <div
          className={cn(
            { 'product-infos__prices': !isLightDisplay },
            {
              'product-infos__prices-light': isLightDisplay,
            },
          )}
        >
          {isShowNetPrice &&
            user?.functionality_accesses?.general_show_net_price && (
              <ProductPrice
                className={cn('product__net-price')}
                context="product-net-price"
                discountPrice={discountPrice}
                invoiceUnit={invoiceUnit}
                label={t('common:entities:product.price.customer_price')}
                price={customerPrice}
                showEmptyLabel
                weeeAmount={weeeAmount}
                sup={false}
              />
            )}

          {publicPrice !== undefined &&
            publicPrice > 0 &&
            user.role !== ROLES.CUSTOMER_GUEST && (
              <ProductPrice
                className={cn(
                  {
                    'product__public-price': user.role !== ROLES.CUSTOMER_GUEST,
                  },
                  {
                    'product__public-price-discount': discountPrice,
                  },
                )}
                context="product-public-price"
                invoiceUnit={invoiceUnit}
                label={t('common:entities:product.price.public_price')}
                price={publicPrice}
                weeeAmount={weeeAmount}
                sup={false}
              />
            )}

          {user.role === ROLES.CUSTOMER_GUEST && (
            <ProductPrice
              className="product__public-price"
              label={t('common:entities:product.price.public_price_ttc')}
              price={publicPriceTTC}
              discountPrice={discountPriceTTC}
              showEmptyLabel
              invoiceUnit={invoiceUnit}
            />
          )}

          <Energetics
            energetics={energetics}
            className="product-infos__energetics"
          />
        </div>

        <AgrementFgaz
          agrementFgaz={agrementFgaz}
          className="product-infos__agrement-fgaz"
        />

        {isConfigProduct && !isLightDisplay && !isBundle && (
          <ConfigLinks
            className="product-infos__config-links"
            bundleParents={bundleParents}
            configurations={configurations}
            isBundle={isBundle}
            title={t('ebat:entities:product.details.references.title')}
          />
        )}

        {isBundle && !isLightDisplay && (
          <ConfigLinks
            className="product-infos__config-links"
            configurations={related}
            bundleParents={bundleParents}
            isBundle={isBundle}
            title={t('ebat:entities:product.details.bundle.title')}
          />
        )}
        {hasBundleParents() && !isLightDisplay && (
          <ConfigLinks
            className="product-infos__config-links"
            configurations={bundleParents}
            isBundle={isBundle}
            bundleParents={bundleParents}
            title={t('ebat:entities:product.details.bundle_parents.title')}
          />
        )}

        {substitute && !isLightDisplay && (
          <AlternativeProduct
            className="product-infos__substitute"
            label={t('common:messages:products.substitute')}
            name={substitute.name}
            type={ALTERNATIVE_TYPE.SUBSTITUTE}
            url={urlAlternativeProduct(substitute?.path, substitute?.slug)}
          />
        )}
        {hasProductDiff && !isLightDisplay && (
          <AlternativeProduct
            className="product-infos__diff"
            label={t('common:messages:products.diff')}
            name={productDiff[0].name}
            type={ALTERNATIVE_TYPE.DIFF}
            url={urlAlternativeProduct(
              productDiff[0]?.path,
              productDiff[0]?.slug,
            )}
          />
        )}

        {!isLightDisplay && (
          <>
            <Quantity
              className="product-infos__quantity"
              disabled={isDisabled.addToCart}
              id={id}
              isLoading={isDisabled.loading}
              quantity={salesPackaging && !splitPackaging ? salesPackaging : 1}
              ref={quantityFieldRef}
              salesPackaging={salesPackaging}
              splitPackaging={splitPackaging}
              unit={saleUnit}
              updateOnChange={offsetWidth < BREAKPOINTS.desktop}
              max={isClearance() ? getTotalStock() : undefined}
            />

            <div className="product-infos__stock">
              <p className="stock__label">
                {t('common:entities:product.stock')}
              </p>
              <Stocks
                categories={categories}
                className={cn('stock__value', {
                  'stock__value--hasstock': stocks.length > 0,
                })}
                companyAvailability={companyAvailability}
                status={stockStatus}
                stocks={stocks}
                unit={saleUnit}
              />
            </div>
          </>
        )}
        {salesPackaging && saleUnit && !isLightDisplay && (
          <div className="product-infos__sales-packaging">
            <span className="sales-packaging__label">
              {t('common:entities:product.sale_unit')}
            </span>
            <span className="sales-packaging__value">{`${salesPackaging} ${saleUnit}`}</span>
          </div>
        )}

        <Energetics
          energetics={energetics}
          className="product-infos__energetics-mobile"
        />

        {!isLightDisplay && (
          <ButtonLoading
            className="product-infos__addto"
            context="add-to-cart"
            value={sku}
            disabled={isDisabled.addToCart}
            isLoading={isDisabled.loading}
            onClick={() => addCartItemsAction()}
          >
            <BasketSvg className="addto__svg" />
            {t('common:messages:actions.add_to_cart')}
          </ButtonLoading>
        )}
        {!splitPackaging && !isLightDisplay && (
          <p className="quantity__warning" data-qa-id="quantity-warning">
            {t('common:entities:product.split_packaging')}
          </p>
        )}
      </div>
    </div>
  );
};

ProductInfos.defaultProps = {
  ...ProductInfosDefaultProps,
};

ProductInfos.propTypes = {
  ...ProductInfosPropTypes,
};

export default ProductInfos;
